import { useCallback } from 'react'
import { useSearchParams } from 'wouter'

export const useParamState = <T extends Record<string, string>>(
  values: T,
  defaultValueKey: keyof T,
  queryParamName = 'tab',
) => {
  type ValueKey = keyof T

  const [searchParams, setSearchParams] = useSearchParams()

  const getActiveValue = (): ValueKey => {
    const valueFromUrl = searchParams.get(queryParamName)
    const valueKeys = Object.keys(values)

    if (valueFromUrl && valueKeys.includes(valueFromUrl)) {
      return valueFromUrl
    }

    return defaultValueKey
  }

  const activeValue = getActiveValue()

  const selectValue = useCallback(
    (key: ValueKey) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.set(queryParamName, String(key))
          return newParams
        },
        {
          replace: true,
        },
      )
    },
    [queryParamName, setSearchParams],
  )

  const toggleValue = useCallback(() => {
    const valueKeys = Object.keys(values)
    const currentIndex = valueKeys.indexOf(String(activeValue))
    const nextIndex = (currentIndex + 1) % valueKeys.length
    selectValue(valueKeys[nextIndex])
  }, [values, activeValue, selectValue])

  return [activeValue, selectValue, toggleValue] as const
}

export const useParamSearch = (queryParamName = 'search') => {
  const [searchParams, setSearchParams] = useSearchParams()

  const value = searchParams.get(queryParamName) ?? ''

  const setValue = useCallback(
    (newValue: string) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev)
          if (newValue === '') {
            newParams.delete(queryParamName)
          } else {
            newParams.set(queryParamName, newValue)
          }
          return newParams
        },
        {
          replace: true,
        },
      )
    },
    [queryParamName, setSearchParams],
  )

  return [value, setValue] as const
}
